import { ChangeEvent, useState } from 'react'
import Button from 'components/ui/Button'
import Checkbox from '@mui/material/Checkbox'
import DailyEventCard from './DailyEventCard'
import { IDateCalendar } from '../LocationsEdit'
import { formatUSDate } from 'utils/intlDate'
import { DatesSetArg } from '@fullcalendar/core'
import { TextField } from '@mui/material'
import { getMinutesDifference, newDateFromString } from 'utils/date'
import { trucktypes } from 'constants/trucktypes'
import { enqueueSnackbar } from 'notistack'
import { useBulkReleaseOrdersMutation } from '../../../../services/orders'
import { ILocationAvailabilities } from 'interfaces/locations'
import { useGetLocationAvailabilityQuery } from 'services/classes'

export default function DailyCalendar({
  locationId,
  dailyDate,
  events,
  handleDateChange,
  weeklyView,
  setWeeklyView,
  currentMonth,
  currentYear
}: {
  locationId: string
  dailyDate: IDateCalendar | undefined
  events: any[]
  handleDateChange: (date: DatesSetArg) => void
  weeklyView: string
  setWeeklyView: (view: 'order' | 'truck') => void
  currentMonth: number
  currentYear: number
}) {
  const { data: truckPayload } = useGetLocationAvailabilityQuery({
    locationId: locationId,
    month: currentMonth,
    year: currentYear
  })

  const [isChangedInput, setIsChangedInput] = useState<boolean>(false)
  const [currentDailyDate, setCurrentDailyDate] = useState<Date>(
    dailyDate?.start || new Date()
  )
  const currentTitleDate = new Date(dailyDate?.start || new Date())
  currentTitleDate.setDate((dailyDate?.start || new Date()).getDate() + 1)

  const [checked, setChecked] = useState<string[]>([])
  const groupedEvents = events.reduce((acc: any, event: any) => {
    event.truckAssignments.forEach((assignment: any) => {
      const truckType = assignment.class.truck_type

      if (!acc[truckType]) {
        acc[truckType] = []
      }

      acc[truckType].push({ ...event })
    })

    return acc
  }, {})
  const [releaseOrders, { isLoading }] = useBulkReleaseOrdersMutation()

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleChange = (date: Date, action: string) => {
    let currentDay = new Date(date)
    let dayStart = new Date(date)
    let dayEnd = new Date(date)
    if (action === 'prev') {
      dayStart.setDate(dayStart.getDate() - 1)
      dayEnd.setDate(dayEnd.getDate())
      currentDay.setDate(currentDay.getDate() - 1)
    } else if (action === 'next') {
      dayStart.setDate(dayStart.getDate() + 1)
      dayEnd.setDate(dayEnd.getDate() + 2)
      currentDay.setDate(currentDay.getDate() + 1)
    } else if (action === 'today') {
      dayStart = new Date()
      dayEnd = new Date()
      dayEnd.setDate(dayEnd.getDate() + 1)
      currentDay = new Date()
    }

    setCurrentDailyDate(currentDay)
    handleDateChange({
      start: dayStart,
      end: dayEnd,
      startStr: dayStart.toDateString(),
      endStr: dayEnd.toDateString(),
      timeZone: dayStart.getTimezoneOffset().toString(),
      view: 'dayGridDay' as any
    })
  }

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const dayStart = newDateFromString(e.target.value)
    const dayEnd = newDateFromString(e.target.value)
    dayEnd.setDate(dayEnd.getDate() + 1)

    setIsChangedInput(true)
    setCurrentDailyDate(dayStart)
    handleDateChange({
      start: dayStart,
      end: dayEnd,
      startStr: dayStart.toDateString(),
      endStr: dayEnd.toDateString(),
      timeZone: dayStart.getTimezoneOffset().toString(),
      view: 'dayGridDay' as any
    })
  }

  const handleCheckAll = () => {
    if (checked.length === events.length) {
      setChecked([])
    } else {
      const newChecked = events.map((event) => event.id)
      setChecked(newChecked)
    }
  }

  const handleOrderPrinterRedirect = () => {
    const basePrinterUrl = `${process.env.REACT_APP_SHOPIFY_URL}/apps/order-printer-emailer/print/orders`;

    if (checked.length === 0) {
      enqueueSnackbar('No orders selected for printing', { variant: 'error' });
      return;
    }

    // Get source_order_id from events based on checked order IDs
    const selectedSourceOrderIds = checked
      .map((orderId) => {
        const event = events.find((event) => event.id === orderId);
        return event?.source_order_id;
      })
      .filter((sourceOrderId) => !!sourceOrderId);

    if (selectedSourceOrderIds.length === 0) {
      enqueueSnackbar('No valid source order IDs found for printing', {
        variant: 'error',
      });
      return;
    }

    // Build a single comma-separated string: id=1234,5678,91023
    const orderIdsQuery = `id=${selectedSourceOrderIds.join(',')}`;

    const printerUrl = `${basePrinterUrl}?${orderIdsQuery}`;

    window.open(printerUrl, '_blank', 'noopener,noreferrer');
  };


  const handleBulkReleaseOrders = async () => {
    if (checked.length === 0) {
      enqueueSnackbar('No orders selected for release', { variant: 'error' })
      return
    }

    // Extract the correct orderId for each selected event
    const selectedOrderIds = checked
      .map((checkedId) => {
        const event = events.find((event) => event.id === checkedId)
        return event?.orderId // Use `orderId` instead of `source_order_id`
      })
      .filter((orderId) => !!orderId) // Filter out null/undefined values

    if (selectedOrderIds.length === 0) {
      enqueueSnackbar('No valid order IDs found for release', {
        variant: 'error'
      })
      return
    }

    try {
      // Call the bulk release orders API
      await releaseOrders({ orderIds: selectedOrderIds.join(',') })
      enqueueSnackbar('Orders released successfully', { variant: 'success' })
      setChecked([]) // Reset selected checkboxes
    } catch (error) {
      enqueueSnackbar('Failed to release orders', { variant: 'error' })
      console.error('Error releasing orders:', error)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="fc fc-media-screen fc-direction-ltr fc-theme-standard">
        <div className="fc-toolbar fc-toolbar-ltr">
          <div className="fc-toolbar-chunk">
            <div className="fc-button-group">
              <button
                type="button"
                title="Previous day"
                onClick={() => handleChange(currentDailyDate, 'prev')}
                className="fc-prev-button fc-button fc-button-primary"
              >
                <span
                  className="fc-icon fc-icon-chevron-left"
                  role="img"
                ></span>
              </button>
              <button
                type="button"
                title="Next day"
                onClick={() => handleChange(currentDailyDate, 'next')}
                className="fc-next-button fc-button fc-button-primary"
              >
                <span
                  className="fc-icon fc-icon-chevron-right"
                  role="img"
                ></span>
              </button>
              <button
                type="button"
                title="Today"
                disabled={
                  currentDailyDate.toDateString() === new Date().toDateString()
                }
                onClick={() => handleChange(new Date(), 'today')}
                className="fc-today-button fc-button fc-button-primary"
              >
                today
              </button>
            </div>
          </div>
          <div className="fc-toolbar-chunk">
            <h2 className="fc-toolbar-title" id="fc-dom-86">
              {formatUSDate(
                isChangedInput ? currentDailyDate : currentTitleDate
              )}
            </h2>
          </div>
          <div className="fc-toolbar-chunk">
            <div className="fc-button-group">
              <TextField
                value={currentDailyDate.toISOString().split('T')[0]}
                onChange={handleInputChange}
                name="daySelectInput"
                type="date"
                size="small"
              />
              <button
                type="button"
                title="Order View"
                onClick={() => setWeeklyView('order')}
                className={`fc-orderView-button fc-button fc-button-primary ${
                  weeklyView === 'order' ? 'active' : ''
                }`}
              >
                Order View
              </button>
              <button
                type="button"
                title="Truck View"
                onClick={() => setWeeklyView('truck')}
                className={`fc-truckView-button fc-button fc-button-primary ${
                  weeklyView === 'truck' ? 'active' : ''
                }`}
              >
                Truck View
              </button>
            </div>
          </div>
        </div>
      </div>
      {events.length ? (
        <div className="bg-[#F2F2F2] py-2 px-1 flex gap-1 rounded items-center">
          <Checkbox
            checked={checked.length === events.length}
            onClick={handleCheckAll}
          />
          <div className="flex gap-3">
              <>
                <Button
                  className="!py-1"
                  size="small"
                  onClick={handleBulkReleaseOrders}
                  disabled={!checked.length}
                >
                  Release Deliveries
                </Button>
                <Button
                  className="!py-1"
                  size="small"
                  onClick={handleOrderPrinterRedirect}
                  disabled={!checked.length}
                >
                  Print Deliveries
                </Button>
              </>
          </div>
        </div>
      ) : (
        <div className="bg-[#E3EFF8] p-10 text-[#003B71]">
          There's no data for the selected date.
        </div>
      )}
      {weeklyView === 'order' ? (
        <div className="flex flex-col gap-6">
          {events.map((event) => {
            const labelId = `checkbox-list-label-${event.id}`

            return (
              <DailyEventCard
                key={event.id}
                event={event}
                checked={checked}
                handleToggle={handleToggle}
                labelId={labelId}
              />
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {Object.keys(groupedEvents).map((name) => {
            const groupedCorrectEvents = groupedEvents[name].map(
              (groupedEt: any) => {
                const groupedEtStartSplit = groupedEt.start.split('-')
                const groupedEtStart =
                  groupedEtStartSplit[1] +
                  '-' +
                  groupedEtStartSplit[2] +
                  '-' +
                  groupedEtStartSplit[0]

                const assignedTrucksAtLocation =
                  groupedEt?.assignedTrucksAtLocation.filter(
                    (assignment: any) =>
                      assignment.class.truck_type === Number(name)
                  )

                const trucksOverridden = assignedTrucksAtLocation.map(
                  (AssTrAtLo: any) => {
                    const checkIfTruckIsOverridden = truckPayload?.find(
                      (override: any) => {
                        const findId = override.truck_id === AssTrAtLo.class_id
                        return findId
                      }
                    )

                    if (checkIfTruckIsOverridden) {
                      return {
                        ...AssTrAtLo,
                        minute_capacity:
                          checkIfTruckIsOverridden.availability[
                            groupedEtStart
                          ]?.[0].minutes_available
                      }
                    }

                    const getDayOfWeek = new Date(groupedEt.start).getDay()
                    const getLocationAvailability =
                      groupedEt?.locationAvailabilities.find(
                        (LoAv: ILocationAvailabilities) => {
                          return LoAv.day_of_week === getDayOfWeek
                        }
                      )

                    const minute_capacity = getMinutesDifference(
                      getLocationAvailability.working_time_end,
                      getLocationAvailability.working_time_start
                    )

                    return {
                      ...AssTrAtLo,
                      minute_capacity
                    }
                  }
                )
                return {
                  ...groupedEt,
                  truckAssignments: groupedEt?.truckAssignments.filter(
                    (assignment: any) =>
                      assignment.class.truck_type === Number(name)
                  ),
                  assignedTrucksAtLocation: trucksOverridden
                }
              }
            )

            const totalCalculatedTime = groupedCorrectEvents.reduce(
              (acc: number, event: any) => {
                return (
                  acc +
                  (event.status !== 'completed'
                    ? event.truckAssignments.reduce(
                        (acc: number, assignment: any) =>
                          acc + assignment.calculated_time,
                        0
                      )
                    : 0)
                )
              },
              0
            )

            const truckCapacity =
              groupedCorrectEvents[0].assignedTrucksAtLocation.reduce(
                (acc: number, assignment: any) =>
                  acc + assignment.minute_capacity,
                0
              )

            const remainingTime = truckCapacity - totalCalculatedTime

            return (
              <div key={name}>
                <h3 className="font-semibold text-sm text-[#003B71] border-b border-[#CCCCCC] py-2 flex justify-between mb-6">
                  {trucktypes[name === '1' ? 1 : 0]}
                  <span>
                    {remainingTime}/{truckCapacity}
                  </span>
                </h3>
                <div className="flex flex-col gap-6">
                  {groupedCorrectEvents.map((event: any) => {
                    const labelId = `checkbox-list-label-${event.id}`

                    return (
                      <DailyEventCard
                        key={event.id}
                        event={event}
                        checked={checked}
                        handleToggle={handleToggle}
                        labelId={labelId}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
